import { compact } from "lodash";
import moment from "moment";
import { Artist } from "types/artist";
import { Episode } from "types/episode.types";
import { IndexedObject } from "types/general.types";
import { Plan, Plan2 } from "types/plan.type";
import { UserProfile } from "types/user.types";
import { v4 as uuidv4 } from "uuid";
import getConfig from "next/config";

let sessionId = uuidv4();
let sesionTimestamp = moment().valueOf();
let currentScreen, previousScreen;

export const setCurrentScreen = (pathName: string) => {
  previousScreen = window.location.pathname;
  currentScreen = pathName;
};
export const setPreviousScreen = (pathName: string) =>
  (previousScreen = pathName);

export const getAnalytics = () => {
  if (typeof window !== "undefined" && (window as any).analytics)
    return (window as any).analytics;
  return {
    identify: (data) => {
      console.log(`>>>> Identify: ${JSON.stringify(data)}`);
    },
    track: (eventName: string, data?: IndexedObject) => {
      console.log(`Event: ${eventName}`);
      console.log(`Event data: ${JSON.stringify(data)}`);
    },
    page: (pageName: string) => {
      console.log(`>>>> Page view: ${pageName}`);
    },
    isPlaceholder: true,
  };
};

export const trackEvent = (eventName, properties?: IndexedObject) => {
  const analytics = getAnalytics();
  return analytics.track(eventName, {
    environment: process.env.NEXT_PUBLIC_ENV || "develop",
    current_screen: currentScreen,
    previous_screen: previousScreen,
    session_duration: (moment().valueOf() - sesionTimestamp) / 1000, // In second
    session_id: sessionId,
    source: "WebApp",
    taxonomy_version: "2.0",
    ...(properties || {}),
  });
};

const getEpisodeTrackingProperties = (episode: Episode) => ({
  author_genres: episode.authorGenres,
  author_name: episode.authorName,
  connectivity: "wifi",
  content_categories: episode.categories,
  content_id: episode.id === getConfig().publicRuntimeConfig?.VIDEO_TRAILER_ID ? undefined : episode.id,
  content_length: episode.duration,
  content_pricing_type: episode.isFree ? "Free" : "Paid",
  content_title: episode.title,
  content_type: episode.trackingType,
  position: 0,
  mindset_id: episode.authorId,
});

const getMindsetTrackingProperties = (mindset: Artist) => ({
  mindsetId: mindset.id,
  contentType: "mindset",
  authorName: compact([
    mindset.author?.firstName,
    mindset.author?.lastName,
  ]).join(" "),
  contentTitle: mindset.name ?? "",
  contentCategories: mindset.categories ?? [],
});

const getProductTrackingProperties = (product: Plan | Plan2, params) => ({
  currencyCode: "USD",
  currency: product.currency,
  price: product.priceInUSD,
  discountedPrice: product.discountedPrice,
  monthlyPrice: product.priceInUSD / 12,
  discountedMonthlyPrice: product.discountedPrice / 12,
  revenue: product.discountedPrice,
  fb_content_type: "product",
  ...params,
});

export const trackMediaEvent = (
  eventName: string,
  episode: Episode,
  params
) => {
  const properties = getEpisodeTrackingProperties(episode);
  trackEvent(eventName, {
    ...properties,
    ...params,
  });
};

export const trackPurchaseEvent = (
  eventName,
  {
    mindset,
    episode,
    product,
    params,
  }: {
    mindset?: Artist;
    episode?: Episode;
    product?: Plan | Plan2;
    params?: IndexedObject;
  }
) => {
  let episodeProperties = {};
  let mindsetProperties = {};
  let productProperties = {};
  if (episode) {
    episodeProperties = getEpisodeTrackingProperties(episode);
  }
  if (mindset) {
    mindsetProperties = getMindsetTrackingProperties(mindset);
  }
  if (product) {
    productProperties = getProductTrackingProperties(product, {});
  }
  trackEvent(eventName, {
    ...episodeProperties,
    ...mindsetProperties,
    ...productProperties,
    ...(params || {}),
  });
};

type TrackAccountDeletionEventProps = {
  paymentPlan?: string;
  user: UserProfile;
};
export const trackAccountDeletionEvent = (
  eventName: string,
  { paymentPlan = "year", user }: TrackAccountDeletionEventProps
) => {
  trackEvent(eventName, {
    payment_plan: paymentPlan,
    duration_of_stay: moment().diff(moment(user.createdAt), "days"),
  });
};
